/**
 * useNotifyPrompt - the "who does this email?" gate, as one call.
 *
 * Drops the admin / super admin notify prompt in front of any save that would
 * fan out email. A call site asks who the pending save would reach, the admin
 * answers, and the answer comes back as payload keys to spread onto the save:
 *
 *	 const { requestNotifyDecision, notifyPrompt } = useNotifyPrompt({ lead: … });
 *
 *	 const decision = await requestNotifyDecision({ kind: 'work_order', id, assigneeId });
 *	 if (!decision) return;					   // admin cancelled — save nothing
 *	 await workOrderService.update(id, { ...payload, ...decision });
 *
 *	 return (<>{…the form…}{notifyPrompt}</>);
 *
 * Three things it decides for you, so no call site has to:
 *
 *	 - Non-admins never see it. They get {} — no flags, current behaviour, and
 *	   the server ignores the flags from them anyway.
 *	 - A save that would email nobody doesn't prompt. An empty "nobody will be
 *	   emailed" dialog on every edit is friction with no decision in it.
 *	 - A failed preview doesn't block the save. If the lookup errors the save
 *	   proceeds as it always did rather than stranding the user behind a broken
 *	   gate — the notification defaults are the safe side of that trade.
 *
 * The promise settles only once the admin answers, so callers read as straight
 * line code. Errors from the save itself stay with the caller, which already
 * has somewhere to put them (the form's error banner, the page's alert).
 */
import React, { useCallback, useRef, useState } from 'react';
import { NotifyUsersModal } from '../components/organisms/NotifyUsersModal';
import { notificationService } from '../services/notificationService';
import type { NotifyDecision, NotifyPreview, NotifyPreviewRequest } from '../services/notificationService';
import { NOTIFY_CONTROL_ROLES } from '../utils/constants';
import { useAuth } from './useAuth';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface UseNotifyPromptOptions {
	/** Header text. Defaults to "Notify users". */
	title?: string;
	/** Sentence prefix over the list, e.g. "Saving this work order will email". */
	lead?: string;
}

export interface UseNotifyPrompt {
	/**
	 * Ask who the pending save would email, and wait for the answer.
	 *
	 * Resolves with the payload keys to spread onto the save, or null when the
	 * admin cancelled — in which case nothing should be saved.
	 */
	requestNotifyDecision: (request: NotifyPreviewRequest) => Promise<NotifyDecision | null>;
	/** Render this alongside the form; it's null until the prompt is needed. */
	notifyPrompt: React.ReactNode;
	/** Whether this user gets the prompt at all. */
	canControlNotifications: boolean;
}

/* ============================================
 * HOOK
 * ============================================ */

const EMPTY_PREVIEW: NotifyPreview = { events: [], recipients: [] };

export function useNotifyPrompt(options: UseNotifyPromptOptions = {}): UseNotifyPrompt {
	const { hasAnyRole } = useAuth();
	const canControlNotifications = hasAnyRole([...NOTIFY_CONTROL_ROLES]);

	const [isOpen, setIsOpen] = useState(false);
	const [preview, setPreview] = useState<NotifyPreview>(EMPTY_PREVIEW);

	/* The pending promise's resolver. A ref, not state: settling it must not
	   depend on a re-render having happened first. */
	const resolverRef = useRef<((decision: NotifyDecision | null) => void) | null>(null);

	const settle = useCallback((decision: NotifyDecision | null) => {
		setIsOpen(false);
		const resolve = resolverRef.current;
		resolverRef.current = null;
		resolve?.(decision);
	}, []);

	const requestNotifyDecision = useCallback(
		async (request: NotifyPreviewRequest): Promise<NotifyDecision | null> => {
			/* No flags at all for non-admins — an absent key means "no opinion",
			   which is exactly the pre-existing behaviour. */
			if (!canControlNotifications) return {};

			let resolved: NotifyPreview;
			try {
				resolved = await notificationService.previewRecipients(request);
			} catch (err) {
				/* Fail open. A preview outage is not a reason to block a save,
				   and the server still applies every notification preference. */
				console.error('[useNotifyPrompt] Failed to resolve notification recipients:', err);
				return {};
			}

			// Nothing to decide — don't make them dismiss an empty dialog.
			if (resolved.recipients.length === 0) return {};

			setPreview(resolved);
			setIsOpen(true);

			return new Promise<NotifyDecision | null>((resolve) => {
				resolverRef.current = resolve;
			});
		},
		[canControlNotifications],
	);

	const notifyPrompt = isOpen ? (
		<NotifyUsersModal isOpen={isOpen} recipients={preview.recipients} events={preview.events} title={options.title} lead={options.lead} onConfirm={(emails) => settle({ notifyUsers: true, notifyEmails: emails })} onSkip={() => settle({ notifyUsers: false })} onCancel={() => settle(null)} />
	) : null;

	return { requestNotifyDecision, notifyPrompt, canControlNotifications };
}

export default useNotifyPrompt;
